import React, { useRef, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Login from './Login';
import Studio from './studio';
import Explorer from './Explorer';
import Profile from './Profile';
import Learn from './Learn';
import Header from './Header';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';

// CodeMirror Imports
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { oneDark } from "@codemirror/theme-one-dark";

// Modern, Dark Theme Colors
const COLORS = {
  primary: '#6366f1',
  primaryHover: '#4f46e5',
  secondary: '#8b5cf6',
  accent: '#ec4899',
  bg: {
    primary: '#0f0f0f',
    secondary: '#1a1a1a',
    tertiary: '#262626',
    card: '#1f1f1f',
  },
  text: {
    primary: '#f5f5f5',
    secondary: '#a3a3a3',
    tertiary: '#737373',
  },
  border: '#2a2a2a',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
};

// CSS Animasyon Stili (Sayfaya Inject edilecek)
const animationStyles = `
  @keyframes heroFlash {
    0% { background-color: rgba(60, 255, 60, 0.4); border-left-color: #0f0; }
    100% { background-color: transparent; border-left-color: transparent; }
  }

  .active-hero-line {
    animation: heroFlash 0.4s ease-out forwards;
    border-left: 4px solid transparent;
    position: relative;
    z-index: 1;
  }
`;

function HomePage() {
  const navigate = useNavigate();
  const [user, setUser] = React.useState(null);
  const [authLoading, setAuthLoading] = React.useState(true);

  // Animasyon için Editor Referansı
  const editorRef = useRef(null);

  // Hero bölümündeki temizlenmiş örnek kod
  const heroCode = `bpm = 128
swing = 0.2

{
  kick(C1).volume(100).distortion(0.4)
  snare(D1).reverb(0.3).pan(0.1)
  hihat(F#1(3,8)).eq(5,0,0)
}*4`;

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Simüle edilmiş "Oynatma" animasyonu
  useEffect(() => {
    // Animasyonun çalışacağı satır indeksleri (0 tabanlı)
    // 3: kick, 4: snare, 5: hihat
    const linesToAnimate = [4, 5, 6];
    let currentIndex = 0;

    const interval = setInterval(() => {
      if (!editorRef.current || !editorRef.current.view) return;

      const view = editorRef.current.view;
      // CodeMirror DOM içindeki satırları bul
      const domLines = view.contentDOM.querySelectorAll(".cm-line");

      if (domLines.length > 0) {
        // Önce hepsini temizle
        domLines.forEach(line => line.classList.remove("active-hero-line"));

        // Sıradaki satırı yak
        const targetIndex = linesToAnimate[currentIndex];
        if (domLines[targetIndex]) {
          domLines[targetIndex].classList.add("active-hero-line");
        }

        // Bir sonraki adıma geç (Döngü)
        currentIndex = (currentIndex + 1) % linesToAnimate.length;
      }
    }, 500); // 500ms hızında (yaklaşık 120bpm hissi)

    return () => clearInterval(interval);
  }, []);

  if (authLoading) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100vh',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#0f0f0f', color: '#00d8ff', fontFamily: 'sans-serif', zIndex: 99999
      }}>
        <style>
          {`
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            @keyframes pulse { 0% { opacity: 0.6; } 50% { opacity: 1; } 100% { opacity: 0.6; } }
          `}
        </style>
        <div style={{
          width: '50px', height: '50px', border: '3px solid rgba(0, 216, 255, 0.3)',
          borderTop: '3px solid #00d8ff', borderRadius: '50%',
          animation: 'spin 1s linear infinite', marginBottom: '20px'
        }}></div>
        <div style={{
          fontSize: '24px', fontWeight: 'bold', letterSpacing: '2px',
          textTransform: 'uppercase', animation: 'pulse 1.5s ease-in-out infinite'
        }}>
          SoundCode
        </div>
      </div>
    );
  }

  // Mock data
  const featuredTracks = [
    { id: 1, title: 'Midnight Dreams', artist: 'producer_alex', plays: 15420, likes: 3245, genre: 'Electronic' },
    { id: 2, title: 'Lo-Fi Study', artist: 'beatmaker_sam', plays: 12890, likes: 2876, genre: 'Lo-Fi' },
    { id: 3, title: 'Ambient Flow', artist: 'sound_designer', plays: 11200, likes: 2654, genre: 'Ambient' },
  ];

  const stats = [
    { label: 'Active Producers', value: '12,482', icon: '👤' },
    { label: 'Tracks Created', value: '48,291', icon: '🎵' },
    { label: 'Code Runs', value: '2.8M+', icon: '⚡' },       // Hours Listened yerine: Platformun canlılığını gösterir
    { label: 'Lessons Learned', value: '15,400+', icon: '🎓' }, // Community Members yerine: Eğitimi vurgular
  ];

  return (
    <div style={styles.container}>
      {/* Stil Tagını Inject Et */}
      <style>{animationStyles}</style>

      <Header user={user} />

      {/* Main Content */}
      <main style={styles.main}>
        {/* Hero Section */}
        <section style={styles.hero}>
          <div style={styles.heroContent}>
            <h1 style={styles.heroTitle}>
              Create Music with Code
            </h1>
            <p style={styles.heroSubtitle}>
              Transform your ideas into sound. Join thousands of producers creating, sharing, and learning together on soundCode.
            </p>
            <div style={styles.heroActions}>
              <button
                style={styles.btnPrimaryLarge}
                onClick={() => user ? navigate('/studio') : navigate('/login')}
              >
                Start Creating
              </button>
              <button
                style={styles.btnSecondaryLarge}
                onClick={() => navigate('/explorer')}
              >
                Explore Tracks
              </button>
            </div>
          </div>

          {/* Hero Visual - CodeMirror Editor */}
          <div style={styles.heroVisual}>
            <div style={styles.visualCard}>
              <div style={styles.visualCardHeader}>
                <div style={styles.visualDots}>
                  <span style={styles.dot}></span>
                  <span style={styles.dot}></span>
                  <span style={styles.dot}></span>
                </div>
                {/* DEĞİŞİKLİK: Dosya adı my_melody.sc oldu */}
                <span style={styles.visualCardTitle}>my_melody.sc</span>
              </div>
              {/* CodeMirror Editörü (Read Only) */}
              <div style={{ height: '300px', fontSize: '14px' }}>
                <CodeMirror
                  ref={editorRef} // Referans eklendi
                  value={heroCode}
                  height="300px"
                  theme={oneDark}
                  extensions={[javascript()]}
                  editable={false}
                  onCreateEditor={(view) => { editorRef.current = { view }; }} // View'a erişim
                  basicSetup={{ lineNumbers: true, foldGutter: false, highlightActiveLine: false }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section style={styles.statsSection}>
          {stats.map((stat, index) => (
            <div key={index} style={styles.statCard}>
              <span style={styles.statIcon}>{stat.icon}</span>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{stat.value}</div>
                <div style={styles.statLabel}>{stat.label}</div>
              </div>
            </div>
          ))}
        </section>

        {/* Featured Tracks */}
        {/* Featured Tracks - SADELEŞTİRİLMİŞ VERSİYON */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Featured Tracks</h2>
            <button
              style={styles.linkButton}
              onClick={() => navigate('/explorer')}
            >
              View all →
            </button>
          </div>

          <div style={styles.trackGrid}>
            {featuredTracks.map((track) => (
              <div
                key={track.id}
                style={styles.trackCard}
              >
                {/* Görsel Alanı (Artık daha küçük ve yatay) */}
                <div style={styles.trackCover}>
                  <button style={styles.playButton}>
                    <span>▶</span>
                  </button>
                </div>

                {/* Bilgi Alanı (Genre kaldırıldı, daha kompakt) */}
                <div style={styles.trackInfo}>
                  <div>
                    <h3 style={styles.trackTitle}>{track.title}</h3>
                    <p style={styles.trackArtist}>{track.artist}</p>
                  </div>
                  <div style={styles.trackMeta}>
                    {/* Sadece dinlenme sayısı ve kalp ikonu eklendi, tür kaldırıldı */}
                    <span style={styles.trackStat}>♥ {track.likes}</span>
                    <span style={styles.trackStat}>▶ {track.plays.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Features Section */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Why soundCode?</h2>
          <div style={styles.featureGrid}>
            <div style={styles.featureCard}>
              <div style={styles.featureIcon}>💻</div>
              <h3 style={styles.featureTitle}>Create Music</h3>
              <p style={styles.featureText}>
                No complex boilerplate. Create music using our easy syntax. Just your code editor and your creativity.
              </p>
            </div>
            <div style={styles.featureCard}>
              <div style={styles.featureIcon}>🌍</div>
              <h3 style={styles.featureTitle}>Global Community</h3>
              <p style={styles.featureText}>
                Share your creations and learn from thousands of producers worldwide.
              </p>
            </div>
            <div style={styles.featureCard}>
              <div style={styles.featureIcon}>📚</div>
              <h3 style={styles.featureTitle}>Learn & Grow</h3>
              <p style={styles.featureText}>
                Access tutorials and challenges to improve your skills.
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>

          <div style={styles.footerLeft}>
            <div style={styles.logoSection} onClick={() => navigate('/')}>
              <div style={styles.logoIconWrapper}>
                <span style={styles.bracketLeft}>&lt;</span>
                <span style={styles.bracketRight}>&gt;</span>
              </div>
              <div style={styles.logoTextWrapper}>
                <span style={styles.textSound}>Sound</span>
                <span style={styles.textCode}>Code</span>
              </div>
            </div>
            <p style={{ ...styles.footerText, marginTop: '16px' }}>
              Create music with code. Join the community.
            </p>
          </div>

          <div style={styles.footerLinksContainer}>
            {/* DEĞİŞİKLİK: Yazı rengi secondary (#a3a3a3) yapıldı */}
            <a style={styles.footerLinkLarge} onClick={() => navigate('/explorer')}>Explorer</a>
            <a style={styles.footerLinkLarge} onClick={() => navigate('/studio')}>Studio</a>
            <a style={styles.footerLinkLarge} onClick={() => navigate('/learn')}>Learn</a>
          </div>

        </div>

        <div style={styles.footerBottom}>
          <p style={styles.footerCopy}>© 2026 soundCode. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/signin" element={<Login />} />
        <Route path="/signup" element={<Login />} />
        <Route path="/login" element={<Navigate to="/signin" />} />
        <Route path="/studio" element={<Studio />} />
        <Route path="/explorer" element={<Explorer />} />
        <Route path="/profile" element={<ProfileWrapper />} />
        <Route path="/learn" element={<Learn />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
}

function ProfileWrapper() {
  const navigate = useNavigate();
  return (
    <Profile
      onBack={() => navigate('/')}
      onEditTrack={(track) => {
        navigate('/studio', { state: { editTrack: track } });
      }}
    />
  );
}

const styles = {
  // ... (Mevcut loading stilleri)
  loadingContainer: {
    minHeight: "100vh",
    backgroundColor: COLORS.bg.primary,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: COLORS.text.primary
  },
  loadingSpinner: {
    fontSize: "64px",
    animation: "spin 1s linear infinite"
  },
  loadingText: {
    color: COLORS.text.secondary,
    fontSize: "18px",
    marginTop: "20px"
  },
  container: {
    minHeight: '100vh',
    backgroundColor: COLORS.bg.primary,
    color: COLORS.text.primary,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  btnPrimaryLarge: {
    padding: '14px 32px',
    backgroundColor: COLORS.primary,
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
  },
  btnSecondaryLarge: {
    padding: '14px 32px',
    backgroundColor: 'transparent',
    color: COLORS.text.primary,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
  },
  linkButton: {
    backgroundColor: 'transparent',
    color: COLORS.primary,
    border: 'none',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  main: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 32px',
  },
  hero: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '80px',
    alignItems: 'center',
    padding: '120px 0',
  },
  heroContent: {
    maxWidth: '600px',
  },
  heroTitle: {
    fontSize: '64px',
    fontWeight: '800',
    lineHeight: '1.1',
    marginBottom: '24px',
    background: `linear-gradient(135deg, ${COLORS.text.primary}, ${COLORS.text.secondary})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  heroSubtitle: {
    fontSize: '20px',
    lineHeight: '1.6',
    color: COLORS.text.secondary,
    marginBottom: '40px',
  },
  heroActions: {
    display: 'flex',
    gap: '16px',
  },
  heroVisual: {
    position: 'relative',
  },
  visualCard: {
    backgroundColor: "#282c34",
    border: `1px solid ${COLORS.border}`,
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  visualCardHeader: {
    padding: '10px 16px',
    borderBottom: `1px solid #111`,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: '#21252b'
  },
  visualDots: {
    display: 'flex',
    gap: '6px',
  },
  dot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: COLORS.bg.tertiary,
  },
  visualCardTitle: {
    fontSize: '12px',
    color: COLORS.text.secondary,
    fontFamily: 'monospace'
  },
  statsSection: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '24px',
    padding: '80px 0',
    borderTop: `1px solid ${COLORS.border}`,
    borderBottom: `1px solid ${COLORS.border}`,
  },
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '24px',
    backgroundColor: COLORS.bg.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '12px',
  },
  statIcon: {
    fontSize: '32px',
  },
  statContent: {
    flex: 1,
  },
  statValue: {
    fontSize: '28px',
    fontWeight: '700',
    marginBottom: '4px',
  },
  statLabel: {
    fontSize: '13px',
    color: COLORS.text.secondary,
  },
  section: {
    padding: '80px 0',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '40px',
  },
  sectionTitle: {
    fontSize: '36px',
    fontWeight: '700',
  },
  trackGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '20px', // Boşluk biraz daraltıldı
  },
  trackCard: {
    backgroundColor: COLORS.bg.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '8px', // Köşeler biraz daha keskinleştirildi
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.2s, border-color 0.2s',
    display: 'flex',       // Dikey düzeni koruyoruz ama flex ile
    flexDirection: 'column',
    // Hover efekti eklenebilir
    ':hover': {
      borderColor: COLORS.primary
    }
  },
  trackCover: {
    width: '100%',
    height: '100px', // YÜKSEKLİK SABİTLENDİ VE KÜÇÜLTÜLDÜ (Eskiden kareydi)
    background: `linear-gradient(to right, ${COLORS.bg.tertiary}, ${COLORS.bg.secondary})`, // Daha soft bir gradient
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  playButton: {
    width: '36px',  // Buton küçültüldü
    height: '36px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)', // Daha şeffaf ve sade
    backdropFilter: 'blur(4px)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '50%',
    color: '#fff',
    fontSize: '14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    // Hover durumunda parlasın
    ':hover': {
      backgroundColor: COLORS.primary,
      borderColor: COLORS.primary
    }
  },
  trackInfo: {
    padding: '12px 16px', // Padding azaltıldı
    display: 'flex',
    justifyContent: 'space-between', // Başlık solda, istatistikler sağda hizalansın diye
    alignItems: 'center',
  },
  trackTitle: {
    fontSize: '15px', // Font biraz küçüldü
    fontWeight: '600',
    marginBottom: '2px',
    color: COLORS.text.primary,
  },
  trackArtist: {
    fontSize: '12px', // Artist fontu küçüldü
    color: COLORS.text.secondary,
    margin: 0, // Alt boşluk kaldırıldı
  },
  trackMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  // trackGenre tamamen kaldırıldı
  trackStat: {
    fontSize: '11px', // İstatistik fontu çok küçültüldü ve minimal yapıldı
    color: COLORS.text.tertiary,
    fontFamily: 'monospace', // Sayılar için monospace font
  },
  featureGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '32px',
    marginTop: '48px',
  },
  featureCard: {
    padding: '32px',
    backgroundColor: COLORS.bg.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '12px',
    textAlign: 'center',
  },
  featureIcon: {
    fontSize: '48px',
    marginBottom: '20px',
  },
  featureTitle: {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '12px',
  },
  featureText: {
    fontSize: '15px',
    lineHeight: '1.6',
    color: COLORS.text.secondary,
  },
  footer: {
    borderTop: `1px solid ${COLORS.border}`,
    marginTop: '80px',
    backgroundColor: '#050505',
  },
  footerContent: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '60px 32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerLeft: {
    display: 'flex',
    flexDirection: 'column',
  },
  footerText: {
    fontSize: '14px',
    color: COLORS.text.secondary,
  },
  footerLinksContainer: {
    display: 'flex',
    gap: '40px',
  },
  footerLinkLarge: {
    fontSize: '16px',
    color: COLORS.text.secondary, // GÜNCELLENDİ: Gri ton (#a3a3a3)
    cursor: 'pointer',
    fontWeight: '500',
    textDecoration: 'none',
    transition: 'color 0.2s',
    '&:hover': {
      color: COLORS.primary
    }
  },
  footerBottom: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '24px 32px',
    borderTop: `1px solid ${COLORS.border}`,
  },
  footerCopy: {
    fontSize: '13px',
    color: COLORS.text.tertiary,
    textAlign: 'center',
  },

  // --- LOGO STİLLERİ ---
  logoSection: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    cursor: "pointer",
    userSelect: "none"
  },
  logoIconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "40px",
    height: "40px",
    backgroundColor: "#111",
    border: "1px solid #333",
    borderRadius: "8px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.5)"
  },
  bracketLeft: {
    color: "#fff",
    fontSize: "18px",
    fontWeight: "bold",
    marginRight: "2px"
  },
  bracketRight: {
    color: "#00d8ff",
    fontSize: "18px",
    fontWeight: "bold"
  },
  logoTextWrapper: {
    display: "flex",
    alignItems: "baseline",
    fontFamily: "'Segoe UI', sans-serif"
  },
  textSound: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#fff",
    letterSpacing: "-0.5px"
  },
  textCode: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#00d8ff"
  }
};

export default App;